<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const langkahKasir = [
  {
    step: 1,
    title: 'Buka POS Kasir',
    icon: 'ri-store-2-line',
    detail: 'Klik menu <strong>POS Kasir</strong> di sidebar kiri, atau klik tombol <strong>"Buka POS Kasir"</strong> dari halaman Penjualan.',
  },
  {
    step: 2,
    title: 'Buka Shift — Hitung Kas Awal',
    icon: 'ri-lock-unlock-line',
    detail: 'Saat Anda masuk POS pertama kali di hari itu, sistem akan otomatis menampilkan dialog <strong>"Buka Shift"</strong>. Hitung dulu uang fisik di laci kasir, lalu masukkan jumlahnya. Klik <strong>"Buka Shift"</strong>.',
  },
  {
    step: 3,
    title: 'Cari & Pilih Produk',
    icon: 'ri-search-line',
    detail: 'Ketik nama produk di kolom pencarian (pojok kiri atas), atau pilih dari kartu produk yang tersedia. Anda juga bisa filter berdasarkan kategori dengan klik chip kategori di bawah kolom pencarian.',
  },
  {
    step: 4,
    title: 'Atur Jumlah di Keranjang',
    icon: 'ri-shopping-basket-line',
    detail: 'Setiap produk yang diklik akan masuk ke keranjang (panel kanan). Gunakan tombol <strong>+</strong> dan <strong>−</strong> untuk ubah jumlah, atau klik ikon <strong>🗑️</strong> untuk hapus item.',
  },
  {
    step: 5,
    title: 'Tambah Catatan atau Diskon (Opsional)',
    icon: 'ri-edit-line',
    detail: 'Klik <strong>"+ Tambah catatan"</strong> di bawah item untuk tulis instruksi khusus (contoh: "tanpa gula"). Untuk diskon seluruh order, isi kolom <strong>"Diskon"</strong> di bawah keranjang.',
  },
  {
    step: 6,
    title: 'Klik "Bayar"',
    icon: 'ri-money-dollar-circle-line',
    detail: 'Klik tombol biru besar <strong>"Bayar (F2)"</strong> di bagian bawah panel kanan. Dialog pembayaran akan muncul.',
  },
  {
    step: 7,
    title: 'Proses Pembayaran',
    icon: 'ri-bank-card-line',
    detail: 'Pilih metode pembayaran (Tunai, QRIS, Transfer, dll). Untuk tunai, klik nominal cepat atau ketik jumlah uang yang diterima. Kembalian dihitung otomatis. Klik <strong>"Bayar Sekarang"</strong>.',
  },
  {
    step: 8,
    title: 'Cetak Struk',
    icon: 'ri-printer-line',
    detail: 'Setelah bayar, struk otomatis muncul. Klik <strong>"Cetak Struk"</strong> untuk print, atau <strong>"Tutup"</strong> untuk langsung ke pesanan berikutnya.',
  },
]

const shortcutKeys = [
  { key: 'F1', fungsi: 'Fokus ke kolom pencarian produk' },
  { key: 'F2', fungsi: 'Buka dialog pembayaran' },
  { key: 'F3', fungsi: 'Kosongkan seluruh keranjang' },
  { key: 'Esc', fungsi: 'Tutup dialog yang sedang terbuka' },
]

const metodePembayaran = [
  { metode: 'Tunai', icon: 'ri-money-dollar-circle-line', color: 'success', keterangan: 'Uang tunai. Sistem otomatis hitung kembalian.' },
  { metode: 'QRIS', icon: 'ri-qr-code-line', color: 'primary', keterangan: 'Scan QR untuk bayar via GoPay, OVO, Dana, dll.' },
  { metode: 'Transfer', icon: 'ri-bank-line', color: 'info', keterangan: 'Transfer bank langsung. Bisa isi nomor referensi.' },
  { metode: 'Debit', icon: 'ri-bank-card-line', color: 'warning', keterangan: 'Mesin EDC / kartu debit.' },
  { metode: 'Kredit', icon: 'ri-bank-card-2-line', color: 'error', keterangan: 'Kartu kredit.' },
]

const faq = [
  {
    q: 'Bolehkah satu transaksi dibayar dengan dua metode sekaligus (misalnya sebagian tunai, sebagian QRIS)?',
    a: 'Boleh. Di dialog pembayaran, klik "+ Tambah Metode Pembayaran" untuk menambah baris pembayaran kedua. Isi masing-masing nominal. Sistem akan hitung apakah total sudah cukup sebelum tombol "Bayar Sekarang" aktif.',
  },
  {
    q: 'Bagaimana jika saya klik produk yang salah dan sudah masuk keranjang?',
    a: 'Klik ikon 🗑️ (tempat sampah) di samping item di keranjang untuk hapus item tersebut. Atau jika ingin hapus semua sekaligus, klik "Kosongkan (F3)" di atas keranjang dan konfirmasi.',
  },
  {
    q: 'Bagaimana jika pelanggan sudah bayar tapi ada item yang salah?',
    a: 'Transaksi yang sudah selesai (status "Selesai") tidak bisa diedit. Solusinya: lakukan Void Order dari halaman Detail Order (menu Penjualan → Semua Order). Setelah di-void, buat transaksi baru yang benar. Butuh permission khusus dari pemilik toko.',
  },
  {
    q: 'Apakah stok otomatis berkurang saat transaksi?',
    a: 'Ya, jika produk menggunakan recipe (komposisi bahan baku), stok bahan baku akan otomatis berkurang sesuai jumlah yang terjual. Jika produk tidak punya recipe, stok tidak terpengaruh.',
  },
  {
    q: 'Bolehkah kasir membuka shift tanpa memberitahu owner?',
    a: 'Secara teknis bisa, asal punya permission "Kasir". Tapi sebaiknya owner selalu memantau riwayat shift dari menu Penjualan → Shift Kasir untuk memastikan kas awal yang diinput akurat.',
  },
  {
    q: 'Apa yang terjadi jika listrik/internet mati di tengah transaksi?',
    a: 'Keranjang yang belum dibayar akan hilang karena disimpan di memori browser sementara (bukan di server). Sistem belum mendukung offline mode. Pastikan koneksi internet stabil saat melayani transaksi.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">POS Kasir</span>
        </div>
        <RouterLink to="/userguide">
          <VBtn variant="text" prepend-icon="ri-arrow-left-line" size="small" class="mb-4">
            Kembali ke Daftar Modul
          </VBtn>
        </RouterLink>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="success" variant="tonal" size="64" rounded="lg" class="me-4">
            <VIcon icon="ri-shopping-cart-line" size="36" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-1">
              POS Kasir
            </h4>
            <p class="text-body-1 text-medium-emphasis mb-0">
              Panduan lengkap melayani transaksi penjualan harian
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line" class="mb-0">
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda menggunakan fitur kasir PintuKasir dari mulai buka shift pagi,
          melayani transaksi, proses pembayaran, hingga tutup shift di akhir hari.
          Ikuti urutan langkah di halaman ini agar operasional kasir berjalan lancar.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep Shift -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="success" />
            Konsep Penting: Apa itu Shift Kasir?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Shift kasir</strong> adalah satu periode kerja kasir — dari pertama membuka laci kas
              sampai tutup di akhir giliran kerja. Analoginya seperti buku kas harian di warung:
              Anda tulis berapa uang di laci saat buka, lalu hitung lagi saat tutup.
              Selisihnya menunjukkan apakah ada uang yang kurang atau lebih.
            </p>
            <p class="text-body-2 mb-4">
              Di PintuKasir, sistem ini terjadi otomatis. Anda cukup:
            </p>

            <VRow dense>
              <VCol cols="12" md="4">
                <VCard color="success" variant="tonal" class="h-100">
                  <VCardText class="text-center pa-4">
                    <VIcon icon="ri-sun-line" size="36" color="success" class="mb-2" />
                    <h6 class="text-h6 font-weight-bold mb-1">Pagi — Buka Shift</h6>
                    <p class="text-body-2 mb-0">
                      Hitung uang di laci, input sebagai <strong>"Kas Awal"</strong>.
                      Sistem mencatat waktu buka.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="12" md="4">
                <VCard color="primary" variant="tonal" class="h-100">
                  <VCardText class="text-center pa-4">
                    <VIcon icon="ri-store-2-line" size="36" color="primary" class="mb-2" />
                    <h6 class="text-h6 font-weight-bold mb-1">Siang — Transaksi</h6>
                    <p class="text-body-2 mb-0">
                      Kasir melayani pembeli. Semua transaksi tercatat di bawah shift yang aktif.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
              <VCol cols="12" md="4">
                <VCard color="warning" variant="tonal" class="h-100">
                  <VCardText class="text-center pa-4">
                    <VIcon icon="ri-moon-line" size="36" color="warning" class="mb-2" />
                    <h6 class="text-h6 font-weight-bold mb-1">Malam — Tutup Shift</h6>
                    <p class="text-body-2 mb-0">
                      Hitung uang di laci lagi, input sebagai <strong>"Kas Akhir"</strong>.
                      Sistem cek ada selisih atau tidak.
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>

            <VAlert type="warning" variant="tonal" class="mt-4 mb-0">
              <strong>⚠️ Penting:</strong> Kasir <strong>tidak bisa memproses transaksi</strong>
              sebelum shift dibuka. Jika ada kasir yang lapor tidak bisa terima pembayaran,
              cek apakah shift sudah dibuka atau belum.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Gunakan Kasir Step by Step -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-list-ordered" class="me-2" color="success" />
            Cara Melayani Transaksi — Langkah demi Langkah
          </VCardTitle>
          <VCardText>
            <VTimeline
              side="end"
              align="start"
              truncate-line="both"
              density="comfortable"
            >
              <VTimelineItem
                v-for="item in langkahKasir"
                :key="item.step"
                :dot-color="item.step <= 2 ? 'warning' : item.step >= 7 ? 'info' : 'success'"
                fill-dot
                size="small"
              >
                <template #icon>
                  <span class="text-caption font-weight-bold text-white">{{ item.step }}</span>
                </template>
                <div class="d-flex align-center gap-2 mb-1">
                  <VIcon :icon="item.icon" size="18" />
                  <strong class="text-body-2">{{ item.title }}</strong>
                </div>
                <p class="text-body-2 text-medium-emphasis mb-0" v-html="item.detail" />
              </VTimelineItem>
            </VTimeline>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Anatomi Layar Kasir -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-layout-line" class="me-2" color="primary" />
            Tampilan Layar POS Kasir — Penjelasan Area
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Layar kasir dibagi menjadi dua bagian utama:
            </p>

            <VRow>
              <!-- Panel kiri -->
              <VCol cols="12" md="6">
                <VCard variant="outlined" class="mb-3">
                  <VCardTitle class="pa-3 pb-1 text-body-1">
                    <VIcon icon="ri-layout-left-line" class="me-2" color="info" />
                    Panel Kiri — Daftar Produk
                  </VCardTitle>
                  <VCardText>
                    <VList density="compact">
                      <VListItem prepend-icon="ri-search-line">
                        <VListItemTitle class="text-body-2 text-wrap">
                          <strong>Kolom pencarian</strong> — ketik nama produk, hasil muncul langsung (shortcut: tekan F1)
                        </VListItemTitle>
                      </VListItem>
                      <VListItem prepend-icon="ri-price-tag-3-line">
                        <VListItemTitle class="text-body-2 text-wrap">
                          <strong>Filter kategori</strong> — chip di bawah pencarian, klik untuk filter produk per kategori
                        </VListItemTitle>
                      </VListItem>
                      <VListItem prepend-icon="ri-grid-line">
                        <VListItemTitle class="text-body-2 text-wrap">
                          <strong>Kartu produk</strong> — menampilkan foto, nama, dan harga. Klik untuk tambah ke keranjang
                        </VListItemTitle>
                      </VListItem>
                      <VListItem prepend-icon="ri-error-warning-line">
                        <VListItemTitle class="text-body-2 text-wrap">
                          Produk dengan label <VChip size="x-small" color="error" variant="tonal">Stok habis</VChip> tidak bisa diklik
                        </VListItemTitle>
                      </VListItem>
                    </VList>
                  </VCardText>
                </VCard>
              </VCol>

              <!-- Panel kanan -->
              <VCol cols="12" md="6">
                <VCard variant="outlined" class="mb-3">
                  <VCardTitle class="pa-3 pb-1 text-body-1">
                    <VIcon icon="ri-layout-right-line" class="me-2" color="success" />
                    Panel Kanan — Keranjang & Pembayaran
                  </VCardTitle>
                  <VCardText>
                    <VList density="compact">
                      <VListItem prepend-icon="ri-user-line">
                        <VListItemTitle class="text-body-2 text-wrap">
                          <strong>Nama pelanggan</strong> (opsional) — untuk personalisasi struk
                        </VListItemTitle>
                      </VListItem>
                      <VListItem prepend-icon="ri-shopping-basket-line">
                        <VListItemTitle class="text-body-2 text-wrap">
                          <strong>Daftar item</strong> — semua produk yang dipilih, lengkap dengan +/- jumlah dan catatan per item
                        </VListItemTitle>
                      </VListItem>
                      <VListItem prepend-icon="ri-percent-line">
                        <VListItemTitle class="text-body-2 text-wrap">
                          <strong>Kolom diskon</strong> — diskon dalam Rupiah untuk seluruh order (bukan per item)
                        </VListItemTitle>
                      </VListItem>
                      <VListItem prepend-icon="ri-money-dollar-circle-line">
                        <VListItemTitle class="text-body-2 text-wrap">
                          <strong>Total &amp; tombol Bayar</strong> — total dihitung otomatis, termasuk pajak jika dikonfigurasi
                        </VListItemTitle>
                      </VListItem>
                    </VList>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>

            <!-- Navbar POS -->
            <VCard variant="outlined">
              <VCardTitle class="pa-3 pb-1 text-body-1">
                <VIcon icon="ri-menu-line" class="me-2" color="grey" />
                Bar Atas — Informasi Shift
              </VCardTitle>
              <VCardText>
                <p class="text-body-2 mb-0">
                  Bar hijau di bagian atas layar POS menampilkan:
                  nama kasir yang sedang bertugas, jam buka shift, dan jam saat ini.
                  Ada tombol <strong>"Keluar POS"</strong> di pojok kanan untuk kembali ke halaman admin.
                  Jika keranjang ada isinya, sistem akan meminta konfirmasi sebelum keluar.
                </p>
              </VCardText>
            </VCard>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Metode Pembayaran -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-bank-card-line" class="me-2" color="primary" />
            Metode Pembayaran yang Didukung
          </VCardTitle>
          <VCardText>
            <VRow dense>
              <VCol
                v-for="m in metodePembayaran"
                :key="m.metode"
                cols="12"
                sm="6"
                md="4"
              >
                <VCard variant="outlined" class="h-100">
                  <VCardText class="d-flex align-start gap-3 pa-3">
                    <VAvatar :color="m.color" variant="tonal" size="40" rounded>
                      <VIcon :icon="m.icon" size="22" />
                    </VAvatar>
                    <div>
                      <p class="text-body-2 font-weight-bold mb-1">{{ m.metode }}</p>
                      <p class="text-caption text-medium-emphasis mb-0">{{ m.keterangan }}</p>
                    </div>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>

            <VAlert type="success" variant="tonal" class="mt-4 mb-0">
              <strong>💡 Bayar dengan dua metode:</strong>
              Anda bisa terima pembayaran dari dua sumber sekaligus. Contoh: pelanggan bayar
              Rp 50.000 tunai + Rp 25.000 via QRIS untuk total Rp 75.000.
              Klik <strong>"+ Tambah Metode Pembayaran"</strong> di dalam dialog bayar.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Buka & Tutup Shift Detail -->
    <VRow class="mt-4">
      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lock-unlock-line" class="me-2" color="warning" />
            Detail: Cara Buka Shift
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">Hitung uang fisik di laci kasir Anda.</li>
              <li class="mb-2">Buka halaman <strong>POS Kasir</strong> — dialog "Buka Shift" otomatis muncul.</li>
              <li class="mb-2">
                Isi kolom <strong>"Kas Awal (Opening Cash)"</strong>
                dengan jumlah uang yang ada di laci. Contoh: <strong>Rp 500.000</strong>
              </li>
              <li class="mb-2">Klik <strong>"Buka Shift"</strong>.</li>
              <li class="mb-0">Tampilan POS kasir langsung aktif dan siap terima transaksi.</li>
            </ol>
            <VAlert type="info" variant="tonal" density="compact" class="text-body-2">
              Satu kasir hanya bisa punya <strong>satu shift aktif</strong> dalam satu waktu.
              Shift lama harus ditutup dulu sebelum buka shift baru.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lock-line" class="me-2" color="error" />
            Detail: Cara Tutup Shift
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Tutup shift dilakukan dari halaman <strong>Penjualan → Shift Kasir</strong>
              (bukan dari dalam layar POS). Bisa dilakukan oleh pemilik atau kasir itu sendiri.
            </p>
            <ol class="text-body-2 ps-4 mb-3">
              <li class="mb-2">Buka <strong>Penjualan → Shift Kasir</strong>.</li>
              <li class="mb-2">Temukan shift yang masih berstatus <VChip size="x-small" color="success" variant="tonal">Berjalan</VChip>, lalu klik ikon kunci (🔒).</li>
              <li class="mb-2">Hitung uang tunai di laci, isi kolom <strong>"Kas Akhir (Hasil Hitung Fisik)"</strong>.</li>
              <li class="mb-2">Sistem menampilkan estimasi selisih — apakah kas lebih (<span class="text-info">+</span>), kurang (<span class="text-error">-</span>), atau pas (<span class="text-success">✓</span>).</li>
              <li class="mb-2">Isi <strong>Catatan</strong> jika ada penjelasan selisih (opsional).</li>
              <li class="mb-0">Klik <strong>"Tutup Shift"</strong>.</li>
            </ol>
            <VAlert type="warning" variant="tonal" density="compact" class="text-body-2">
              Setelah shift ditutup, Anda tidak bisa memproses transaksi baru di sesi tersebut.
              Buka shift baru jika masih ada pembeli.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Keyboard Shortcuts -->
    <VRow class="mt-4">
      <VCol cols="12" md="6">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-keyboard-line" class="me-2" color="secondary" />
            Shortcut Keyboard untuk Kasir Cepat
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Untuk kasir yang sudah terbiasa, shortcut keyboard bisa mempercepat transaksi:
            </p>
            <VTable density="compact">
              <thead>
                <tr>
                  <th style="width: 80px">Tombol</th>
                  <th>Fungsi</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in shortcutKeys" :key="s.key">
                  <td>
                    <VChip color="secondary" variant="outlined" size="small" class="font-weight-bold">
                      {{ s.key }}
                    </VChip>
                  </td>
                  <td class="text-body-2">{{ s.fungsi }}</td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Tips -->
      <VCol cols="12" md="6">
        <VCard color="success" variant="tonal" class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips untuk Kasir Efisien
          </VCardTitle>
          <VCardText>
            <VList density="compact" bg-color="transparent">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Pakai shortcut F1 + F2.</strong> Ketik nama produk (F1), klik produk, lalu F2 untuk bayar.
                  Transaksi sederhana bisa selesai dalam 10 detik.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Isi kas awal dengan jujur.</strong> Angka ini jadi dasar laporan selisih kas.
                  Jika sering berbeda, pemilik bisa curiga ada masalah.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Tutup shift setiap ganti kasir</strong> — jangan biarkan satu shift dipakai
                  dua kasir berbeda. Laporan kas jadi tidak akurat.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Layar penuh = fokus.</strong> Halaman POS kasir memakai tampilan layar penuh
                  tanpa menu admin. Minta kasir tidak membuka tab lain saat melayani pembeli.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Peringatan Penting -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="error" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-error-warning-line" class="me-2" />
            ⚠️ Hal-hal yang TIDAK Boleh Dilakukan
          </VCardTitle>
          <VCardText>
            <VList density="compact" bg-color="transparent">
              <VListItem prepend-icon="ri-close-circle-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>JANGAN tutup browser/tab tiba-tiba</strong> saat ada transaksi di keranjang —
                  isi keranjang akan hilang karena belum disimpan ke server.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-close-circle-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>JANGAN input kas awal palsu</strong> (misalnya 0 padahal ada uang di laci) —
                  laporan selisih kas akan salah dan tidak bisa digunakan sebagai dasar rekonsiliasi.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-close-circle-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>JANGAN biarkan shift terus buka berhari-hari</strong> tanpa ditutup —
                  semua transaksi dari hari-hari berbeda akan tercampur dalam satu laporan shift.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-close-circle-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>JANGAN void order sembarangan</strong> — void mengembalikan stok bahan baku
                  dan menghapus catatan transaksi dari laporan harian. Butuh persetujuan pemilik.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Halaman Terkait -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Halaman Terkait
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Penjualan & Riwayat Order', sub: 'Lihat semua transaksi yang pernah terjadi', icon: 'ri-receipt-line', color: 'primary', to: '/userguide/operasional/penjualan' },
              { title: 'Produk & Katalog', sub: 'Pastikan produk sudah diinput sebelum kasir bisa jual', icon: 'ri-cup-line', color: 'info', to: '/userguide/produk-inventory/produk' },
              { title: 'Bahan Baku & Stok', sub: 'Stok otomatis berkurang saat transaksi', icon: 'ri-archive-line', color: 'warning', to: '/userguide/produk-inventory/bahan-baku' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
